import { Box, Center, Flex, IconButton, Image, Skeleton, Spinner, useDisclosure } from '@chakra-ui/react';
import { IMAGE_CREATED } from 'constants/mixpanel';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getInitialWidgetPosition } from 'modules/Editor/Editor.helpers';
import { WidgetDragDropWrapper } from 'modules/Editor/components/WidgetDragDrop';
import { useUserMedia } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import {
  CSSProperties,
  Dispatch,
  ReactElement,
  RefObject,
  SetStateAction,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeGrid as Grid, areEqual } from 'react-window';
import { WidgetType } from 'types/widget.types';
import { isStandalone, isTest } from 'utils/environment';
import { generateDefaultData } from 'widgets/ImageWidget/ImageWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { store } from '../../../../store';
import { selectInfographHeightPx, selectInfographWidthPx } from '../../../../store/infographSelector';
import { COLUMN_NUM, GUTTER_SIZE, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../InfiniteGrid/InfiniteGrid.config';
import DeleteUploadImageAlertDialog from './DeleteUploadImageAlertDialog';
import { DeleteUploadImageIcon } from './DeleteUploadImageIcon';
import { ImageUpload } from './ImageUpload';
import { UploadMenuContextProvider, useUploadMenu } from './UploadMenuContext';

interface UploadImage {
  idx: string;
  url: string;
  mediaId: string;
}

type UploadImagesData = UploadImage[] | undefined;

interface UserImageCellProps<T> {
  columnIndex: number;
  rowIndex: number;
  style: CSSProperties;
  data: T;
}

interface UserImageData {
  uploadImages: UploadImagesData;
  setDeleteMediaId: Dispatch<SetStateAction<string>>;
  onOpenDeleteAlertDialog: () => void;
  disableDragDrop?: boolean;
}

interface UserImageProps {
  idx: string;
  url: string;
  imageRef: RefObject<HTMLImageElement>;
  ignoreFallback?: boolean;
  disableDragDrop?: boolean;
}

interface UserImagesProps {
  uploadImages: UploadImage[] | undefined;
  disableDragDrop?: boolean;
}

type HandleClickUserImage = (event: React.MouseEvent<HTMLButtonElement>) => void;
type HandleClickDelete = (event: React.MouseEvent<HTMLButtonElement>) => void;

interface UploadMenuProps {
  onAddUploadImage: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
}

export const UploadMenu = memo(({ onAddUploadImage, disableDragDrop }: UploadMenuProps): ReactElement => {
  const toast = useToast();

  const showUploadImageError = () =>
    toast({
      title: 'Failed to get user uploaded images',
      status: 'error',
    });

  const { data } = useUserMedia({ onError: !isStandalone ? showUploadImageError : undefined });

  // Think optimization to prevent re-rendering if possible
  const uploadImages: UploadImagesData = useMemo(
    () =>
      data?.map((imageData) => ({
        idx: imageData.id,
        url: `${process.env.REACT_APP_S3_USER_MEDIA_BUCKET_LINK}/${imageData.media_id}`,
        mediaId: imageData.media_id,
      })),
    [data],
  );

  return (
    <UploadMenuContextProvider addUploadImageWidget={onAddUploadImage}>
      <Flex maxH='full' flexDir='column' overflowY='auto'>
        <ImageUpload />
        <UserImages uploadImages={uploadImages} disableDragDrop={disableDragDrop} />
      </Flex>
    </UploadMenuContextProvider>
  );
});
UploadMenu.displayName = 'UploadMenu';

const UserImage = memo(({ idx, url, imageRef, ignoreFallback }: UserImageProps) => {
  return (
    <Image
      key={idx}
      ref={imageRef}
      objectFit='contain'
      width={THUMBNAIL_WIDTH}
      height={THUMBNAIL_HEIGHT}
      src={url}
      role='presentation'
      // need to set pointer events to none here as drag event is triggered for image / svg elements within the draggable container
      pointerEvents='none'
      alt=''
      // pointer events is set in the skeleton for same reason as above
      fallback={<Skeleton width={THUMBNAIL_WIDTH} height={THUMBNAIL_HEIGHT} pointerEvents='none' />}
      // skip loading images in test environment since the fallback can't be mocked
      ignoreFallback={ignoreFallback ?? isTest}
    />
  );
}, areEqual);
UserImage.displayName = 'UserImage';

const UserImageCell = memo(({ columnIndex, rowIndex, style, data }: UserImageCellProps<UserImageData>) => {
  const [isHoverOnDelete, setIsHoverOnDelete] = useState(false);
  const [imageBtnOnFocus, setImageBtnOnFocus] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);

  const cellIdx = rowIndex * COLUMN_NUM + columnIndex;
  const { uploadImages, onOpenDeleteAlertDialog, setDeleteMediaId, disableDragDrop } = data;
  const imageData = uploadImages![cellIdx];

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false });
  const { addUploadImageWidget } = useUploadMenu();

  const createWidgetData = useCallback(() => {
    if (!imageRef.current) return;

    const infographWidthPx = selectInfographWidthPx(store.getState());
    const infographHeightPx = selectInfographHeightPx(store.getState());
    const infographSize = { width: infographWidthPx, height: infographHeightPx };
    const { top, left } = getInitialWidgetPosition();
    const { src, naturalWidth: width, naturalHeight: height } = imageRef.current;

    const widget = generateDefaultData(src, { width, height }, infographSize, top, left);
    return widget;
  }, []);

  const onClickUserImage: HandleClickUserImage = useCallback(() => {
    const widget = createWidgetData();
    if (!widget) return;

    addUploadImageWidget(widget);

    Mixpanel.track(IMAGE_CREATED.eventName, {
      from: IMAGE_CREATED.from.Upload,
    });
  }, [addUploadImageWidget, createWidgetData]);

  const onClickDelete: HandleClickDelete = () => {
    if (!!imageData.mediaId) {
      setDeleteMediaId(imageData.mediaId);
      onOpenDeleteAlertDialog();
    }
  };

  const onImageBtnFocus = () => setImageBtnOnFocus(true);
  // delay removing focus from image button for 100 ms so that focus can be moved to the delete button. Otherwise, the focus would be lost as when imageBtnOnFocus turns to false, delete button would be removed from DOM
  const onImageBtnBlur = () => setTimeout(() => setImageBtnOnFocus(false), 100);

  return (
    imageData && (
      <Flex data-group style={style}>
        <WidgetDragDropWrapper
          isDraggable={!disableDragDrop}
          widgetData={createWidgetData()}
          type={WidgetType.Image}
          dragImage={
            <Box h={style.height} w={style.width}>
              <Center>
                {/* ignore fallback for ghost preview images */}
                <UserImage idx={imageData.idx} url={imageData.url} imageRef={imageRef} ignoreFallback={true} />
              </Center>
            </Box>
          }
          dragImageOffset={{ x: THUMBNAIL_WIDTH / 2, y: THUMBNAIL_HEIGHT / 2 }}
          trackingEventData={{
            event_name: IMAGE_CREATED.eventName,
            properties: {
              from: IMAGE_CREATED.from.Upload,
            },
          }}
        >
          <IconButton
            aria-label={t('sideBar.widget_menu_upload.userImageLabel')}
            variant='icon-btn-side-menu'
            h={style.height}
            w={style.width}
            bgColor={isHoverOnDelete ? 'hover.gray' : 'none'}
            onClick={onClickUserImage}
            onFocus={onImageBtnFocus}
            onBlur={onImageBtnBlur}
            data-testid='insert-user-image'
          >
            <UserImage idx={imageData.idx} url={imageData.url} imageRef={imageRef} />
          </IconButton>
        </WidgetDragDropWrapper>
        <DeleteUploadImageIcon
          imageBtnOnFocus={imageBtnOnFocus}
          onClickDelete={onClickDelete}
          setHover={setIsHoverOnDelete}
        />
      </Flex>
    )
  );
}, areEqual);
UserImageCell.displayName = 'UserImageCell';

const UserImages = ({ uploadImages, disableDragDrop }: UserImagesProps): ReactElement => {
  const userImagesContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const { isOpen, onOpen: onOpenDeleteAlertDialog, onClose: onCloseDeleteAlertDialog } = useDisclosure();
  const [deleteMediaId, setDeleteMediaId] = useState('');

  const onResetDeleteMediaId = () => setDeleteMediaId('');

  // Update the infinite scroll box size when the window is resizing
  useResizeObserver({
    ref: userImagesContainerRef,
    onResize: ({ width = 0, height = 0 }) => {
      setContainerSize({ width, height });
    },
  });

  const uploadImagesData = {
    uploadImages,
    onOpenDeleteAlertDialog,
    setDeleteMediaId,
    disableDragDrop,
  };

  return (
    <Flex ref={userImagesContainerRef} flexDir='column' h='1000' data-testid='user-images-container' overflowY='hidden'>
      {!!uploadImages ? (
        <>
          <Grid
            itemData={uploadImagesData}
            columnCount={COLUMN_NUM}
            columnWidth={GUTTER_SIZE + THUMBNAIL_WIDTH}
            rowHeight={GUTTER_SIZE + THUMBNAIL_HEIGHT}
            height={containerSize.height}
            width={containerSize.width}
            rowCount={Math.ceil(uploadImages.length / COLUMN_NUM)}
            data-testid='upload-images-grid'
          >
            {UserImageCell}
          </Grid>
          <DeleteUploadImageAlertDialog
            isOpen={isOpen}
            onClose={onCloseDeleteAlertDialog}
            onResetId={onResetDeleteMediaId}
            mediaId={deleteMediaId}
          />
        </>
      ) : (
        <Center>
          <Spinner data-testid='upload-images-spinner' />
        </Center>
      )}
    </Flex>
  );
};
